import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiPlus, FiKey, FiLoader } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { keysApisAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import { KeyApiCard, KeysApisFiltros, KeysApisPaginacao } from '../components/keysApis';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing[4]};
`;

const AddButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active {
    transform: translateY(0);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing[6]};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing[4]};
`;

const EmptyTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const EmptyText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const EmptyAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.primary[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
  }
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.error[500]};
  margin-bottom: ${theme.spacing[4]};
`;

const ErrorTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const ErrorText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ErrorAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.error[600]};
  }
`;

const KeysApis = () => {
  const navigate = useNavigate();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  const [filtros, setFiltros] = useState({
    search: '',
    api: '',
    page: 1,
    limit: 10
  });

  // Carregar keys
  const carregarKeys = async (params) => {
    try {
      setLoading(true);
      setError(null);

      const response = await keysApisAPI.listar(params);
      
      if (response.data.success) {
        setKeys(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Erro ao carregar API Keys');
      }
    } catch (err) {
      console.error('Erro ao carregar API Keys:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoading(false);
    }
  };

  // Carregar keys na inicialização
  useEffect(() => {
    carregarKeys(filtros);
  }, []);

  // Atualizar filtros
  const handleFiltrosChange = (novosFiltros) => {
    setFiltros({
      ...novosFiltros,
      page: 1, // Reset para primeira página quando mudar filtros
      limit: filtros.limit || 10 // Manter o limite atual
    });
  };

  // Buscar com filtros
  const handleBuscar = (param = null) => {
    if (param && typeof param === 'object' && typeof param.preventDefault === 'function') {
      param.preventDefault();
      carregarKeys({ ...filtros, page: 1 });
      return;
    }

    const filtrosParaBusca = param || filtros;
    carregarKeys(filtrosParaBusca);
  };

  // Mudar página
  const handlePageChange = (page) => {
    const novosFiltros = { ...filtros, page };
    setFiltros(novosFiltros);
    carregarKeys(novosFiltros);
  };

  // Mudar registros por página
  const handleItemsPerPageChange = (newLimit) => {
    const novosFiltros = { 
      ...filtros, 
      limit: newLimit,
      page: 1 // Reset para primeira página quando mudar o limite
    };
    setFiltros(novosFiltros);
    carregarKeys(novosFiltros);
  };

  // Navegar para adicionar key
  const handleAdicionar = () => {
    navigate('/administracao/keys-apis/novo');
  };

  // Renderizar conteúdo baseado no estado
  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner>
            <FiLoader />
          </LoadingSpinner>
          <LoadingText>Carregando API Keys...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>Erro ao carregar API Keys</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <ErrorAction onClick={() => carregarKeys(filtros)}>
            Tentar novamente
          </ErrorAction>
        </ErrorState>
      );
    }

    if (keys.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <FiKey />
          </EmptyIcon>
          <EmptyTitle>Nenhuma API Key encontrada</EmptyTitle>
          <EmptyText>
            Não foram encontradas API Keys com os filtros aplicados.
          </EmptyText>
          <EmptyAction onClick={handleAdicionar}>
            <FiPlus />
            Adicionar Primeira API Key
          </EmptyAction>
        </EmptyState>
      );
    }

    return (
      <>
        <CardsGrid>
          {keys.map((keyApi) => (
            <KeyApiCard 
              key={keyApi.id} 
              keyApi={keyApi}
            />
          ))}
        </CardsGrid>

        {pagination.totalPages > 1 && (
          <KeysApisPaginacao
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            loading={loading}
          />
        )}
      </>
    );
  };

  return (
    <Layout title="API Keys">
      <Container>
        <Header>
          <AddButton onClick={handleAdicionar}>
            <FiPlus />
            Adicionar API Key
          </AddButton>
        </Header>

        <Content>
          <KeysApisFiltros
            filtros={filtros}
            onFiltrosChange={handleFiltrosChange}
            onBuscar={handleBuscar}
            loading={loading}
          />

          {renderContent()}
        </Content>
      </Container>
    </Layout>
  );
};

export default KeysApis;
